import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { KitElement, KitPattern, KitType, TeamKit } from 'shared/kit';
import FadeIn from 'modules/overlay-canvas/addons/fadeIn';
import ReplaceCarousel from 'modules/overlay-canvas/addons/replacer/replace-carousel';
import ReplaceCommon from 'modules/overlay-canvas/addons/replacer/replace-common';
import ReplaceEvent from 'modules/overlay-canvas/addons/replacer/replace-event';
import { Replacer } from 'modules/overlay-canvas/addons/replacer/replacer-main';
import Static from 'modules/overlay-canvas/canvases/static';
import tournamentLogo from 'shared/assets/placeholders/tournament-logo.png';
import logo1 from 'shared/assets/placeholders/logo1.png';
import logo2 from 'shared/assets/placeholders/logo2.png';

interface IReplacer {
  replacer: Replacer;
  isDoFade?: {
    manager: Static;
  };
}

function replaceFakeData(props: IReplacer) {
  const { replacer, isDoFade } = props;
  const { manager } = isDoFade ?? { manager: undefined };
  const now = Date.now();
  const replaceCommon = new ReplaceCommon(replacer);
  const replaceEvent = new ReplaceEvent(replacer);
  const replaceCarousel = new ReplaceCarousel(replacer);

  replacer.utils.ungroupObjects();

  const fade = new FadeIn();
  let isFadeIn = false;
  let startOpacity = 1;
  if (manager) {
    fade.init(manager);
    replacer.utils.setOpacityAllObjs(0);
    isFadeIn = true;
    startOpacity = 0;
  }

  replaceCommon.replace({
    startOpacity,
    data: {
      championshipName: 'Кубок мира',
      city: 'Новосибирск',
      formatStartDate: {
        time: format(now, 'HH:mm'),
        date: format(now, 'd MMMM', { locale: ru }),
      },
      teams: {
        first: {
          name: 'Слоны',
          logo: logo1,
          lastGamesResults: ['WON', 'DRAW', 'LOST'],
          members: fakePlayersTeam0,
          shortName: 'СЛН',
          kits: FAKE_TEAM_KITS.first,
        },
        second: {
          name: 'Фениксы',
          logo: logo2,
          lastGamesResults: ['LOST', 'DRAW', 'WON'],
          members: fakePlayersTeam1,
          shortName: 'ФНК',
          kits: FAKE_TEAM_KITS.second,
        },
      },
      goals: {
        first: 2,
        second: 7,
      },
      fouls: {
        first: 3,
        second: 4,
      },
      foulsIndicator: {
        first: 3,
        second: 4,
      },
      shootoutGoals: {
        first: 4,
        second: 2,
      },
      shootoutMisses: {
        first: 1,
        second: 2,
      },
      shootoutAttempts: {
        first: ['GOAL', 'GOAL', 'MISS', 'GOAL', 'GOAL'],
        second: ['GOAL', 'MISS', 'GOAL', 'MISS', 'GOAL'],
      },
      organizerLogo: tournamentLogo,
      period: 1,
      periodLabel: '1',
      timer: '14:22',
    },
    isFadeIn,
  });

  replaceEvent.replace({
    startOpacity,
    data: {
      players: {
        first: {
          avatar: avatar,
          lastName: 'Константинопольский',
          name: 'Константин',
          number: 15,
          qtyRedCards: 0,
          qtyYellowCards: 3,
          qtyShootoutGoals: 4,
          middleName: 'Александрович',
          qtyShootoutMisses: 1,
        },
        second: {
          avatar: avatar,
          lastName: 'Михайлов',
          name: 'Михайл',
          number: 21,
          qtyRedCards: 1,
          qtyYellowCards: 2,
          qtyShootoutMisses: 0,
          qtyShootoutGoals: 2,
        },
      },
      teams: {
        team: {
          fouls: 0,
          logo: logo1,
          name: 'СЛОНЫ',
          score: 2,
          shootoutGoals: 5,
          shootoutMisses: 2,
        },
        reversedTeam: {
          fouls: 0,
          logo: logo2,
          name: 'ФЕНИКСЫ',
          score: 7,
          shootoutMisses: 3,
          shootoutGoals: 4,
        },
      },
      eventMinute: 0,
      currentPeriod: 0,
    },
    isFadeIn,
  });

  replaceCarousel.replace({
    data: {
      name: fakePlayersTeam0[0].name,
      position: 'Вратарь',
      avatar: avatar,
      number: fakePlayersTeam0[0].number,
    },
    teamIndex: 0,
    startOpacity,
    isFadeIn,
  });

  replaceCarousel.replace({
    data: {
      name: fakePlayersTeam1[0].name,
      position: 'Вратарь',
      avatar: avatar,
      number: fakePlayersTeam1[0].number,
    },
    teamIndex: 1,
    startOpacity,
    isFadeIn,
  });

  const allObject = manager?.canvas.getObjects().filter((object) => object.opacity === 0 && !object.data?.show) ?? [];
  fade?.fadeIn(allObject);
}

export default replaceFakeData;

const makeFakeKit = (id: number, type: KitType, shirt: string[], shorts: string[], socks: string[]): TeamKit => ({
  id,
  type,
  elements: [
    {
      element: KitElement.SHIRT,
      attributes: { pattern: shirt.length > 1 ? KitPattern.STRIPES_VERTICAL : KitPattern.SOLID, colors: shirt },
    },
    { element: KitElement.SHORTS, attributes: { pattern: KitPattern.SOLID, colors: shorts } },
    { element: KitElement.SOCKS, attributes: { pattern: KitPattern.SOLID, colors: socks } },
  ],
});

/**
 * Комплекты формы для превью в редакторе. У обеих команд есть домашний и гостевой комплекты —
 * так видно результат правила `auto` (команда №1 домашняя, команда №2 гостевая).
 */
export const FAKE_TEAM_KITS: { first: TeamKit[]; second: TeamKit[] } = {
  first: [
    makeFakeKit(1, KitType.HOME, ['#1E5AA8', '#FFFFFF'], ['#1E5AA8'], ['#1E5AA8']),
    makeFakeKit(2, KitType.AWAY, ['#FFFFFF'], ['#1E5AA8'], ['#FFFFFF']),
  ],
  second: [
    makeFakeKit(3, KitType.HOME, ['#C0392B'], ['#C0392B'], ['#C0392B']),
    makeFakeKit(4, KitType.AWAY, ['#F2C230', '#2B2B2B'], ['#2B2B2B'], ['#F2C230']),
  ],
};

const avatar = USER_DEFAULT_AVATAR;

type Player = {
  photo: string;
  name: string;
  number: number;
};

const fakePlayersTeam0: Player[] = [
  {
    photo: avatar,
    name: 'Константин Константинопольский',
    number: 15,
  },
  {
    photo: '',
    name: 'Михаил Михайлов',
    number: 21,
  },
  {
    photo: avatar,
    name: 'Егор Егоров',
    number: 34,
  },
  {
    photo: avatar,
    name: 'Иван Иванов',
    number: 45,
  },
  {
    photo: avatar,
    name: 'Александр Виноградов Александрович',
    number: 77,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 88,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 99,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 100,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 101,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 102,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 103,
  },

  {
    photo: avatar,
    name: 'Александр Александров',
    number: 104,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 105,
  },

  {
    photo: avatar,
    name: 'Александр Александров',
    number: 106,
  },
  {
    photo: avatar,
    name: 'Александр Александров',
    number: 107,
  },

  {
    photo: avatar,
    name: 'Александр Александров',
    number: 108,
  },
];

const fakePlayersTeam1: Player[] = [
  {
    photo: avatar,
    name: 'Криштиану Роналду',
    number: 5,
  },
  {
    photo: avatar,
    name: 'Лионель Месси',
    number: 31,
  },
  {
    photo: avatar,
    name: 'Роберт Левандовский',
    number: 81,
  },
  {
    photo: avatar,
    name: 'Кевин де Брюйне',
    number: 91,
  },
  {
    photo: avatar,
    name: 'Джанлуиджи Буффон',
    number: 99,
  },
  {
    photo: avatar,
    name: 'Криштиану Роналду',
    number: 5,
  },
  {
    photo: avatar,
    name: 'Лионель Месси',
    number: 31,
  },
  {
    photo: avatar,
    name: 'Роберт Левандовский',
    number: 81,
  },
  {
    photo: avatar,
    name: 'Кевин де Брюйне',
    number: 91,
  },
  {
    photo: avatar,
    name: 'Джанлуиджи Буффон',
    number: 99,
  },
];
